import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useMemo } from 'react';
import { toPersianDigits } from '@bookora/shared';
import { Table as AntdTable } from 'antd';
import { TruncatedText } from '../components/affordances.js';
import { Numeric, RANGE_DASH } from '../components/bidi.js';
import { Icon } from '../icons/icon.js';
import { resolveFixed, useUiDirection } from './direction.js';
import { InTableContext } from './in-table-context.js';
function isGroup(column) {
    return Array.isArray(column.children);
}
function resolveFixedValue(fixed, dir) {
    if (fixed === 'start' || fixed === 'end')
        return resolveFixed(fixed, dir);
    return fixed;
}
/**
 * Up / down carets keyed to the order AntD is currently in. `null` means
 * "sortable but unsorted", and it must still render something — an affordance
 * that only appears once you have used it is not an affordance.
 */
function defaultSortIcon({ sortOrder }) {
    if (sortOrder === 'ascend')
        return _jsx(Icon, { name: "chevron-up", size: 16, active: true });
    if (sortOrder === 'descend')
        return _jsx(Icon, { name: "chevron-down", size: 16, active: true });
    return _jsx(Icon, { name: "sort", size: 16 });
}
function defaultFilterIcon(filtered) {
    return _jsx(Icon, { name: "filter", size: 16, active: filtered });
}
/**
 * ── `ellipsis` IS SAFE TO WRITE ─────────────────────────────────────────────
 *
 * `@rc-component/table` 1.11 (`Cell/index.js`) turns `ellipsis: true` into
 * `{ showTitle: true }` and copies a string child — or an element whose own
 * child is a string — into the cell's native `title`; a header cell gets one
 * even under `showTitle: false`. The cell uses `additionalProps.title ??
 * derived`, so an EMPTY string from `onCell`/`onHeaderCell` wins (it is not
 * nullish) and suppresses it.
 *
 * So a truthy `ellipsis` becomes `{ showTitle: false }` plus `title: ''` on
 * both cells, and a plain-string value — no `render`, or a `render` that
 * returns a string — is drawn through `<TruncatedText>`, which shows the full
 * text in the kit's bubble only when it is actually cut. A `render` that
 * returns an element is the column's own business and is left alone.
 */
function normaliseEllipsis(column) {
    if (!column.ellipsis)
        return column;
    const { onCell, onHeaderCell, render } = column;
    const truncate = (value) => typeof value === 'string' && value !== '' ? _jsx(TruncatedText, { children: value }) : value;
    return {
        ...column,
        ellipsis: { showTitle: false },
        onCell: (record, index) => ({ ...onCell?.(record, index), title: '' }),
        onHeaderCell: (col) => ({ ...onHeaderCell?.(col), title: '' }),
        render: render
            ? (value, record, index) => {
                const out = render(value, record, index);
                return typeof out === 'string' ? truncate(out) : out;
            }
            : (value) => truncate(value),
    };
}
function decorateColumns(columns, dir) {
    return columns.map((column) => {
        const fixed = resolveFixedValue(column.fixed, dir);
        if (isGroup(column)) {
            const { children, ...groupRest } = column;
            return {
                ...groupRest,
                fixed,
                children: decorateColumns(children, dir),
            };
        }
        const next = normaliseEllipsis({
            ...column,
            fixed,
        });
        if (next.sorter && next.sortIcon === undefined)
            next.sortIcon = defaultSortIcon;
        if ((next.filters || next.filterDropdown) && next.filterIcon === undefined) {
            next.filterIcon = defaultFilterIcon;
        }
        return next;
    });
}
/**
 * ── OVERFLOW IS A TABLE DECISION, NOT A SCREEN'S ────────────────────────────
 *
 * 41 of the 66 tables in the two panels shipped with no `scroll` at all, so a
 * long Persian value — «محمدحسین شریعتمداری نیاسر» in a patient column,
 * «تزریق ژل هیالورونیک اسید گونه» in a procedure column — either wrapped every
 * cell to two or three lines (tripling the row height, which is exactly what
 * the clinic panel's density contract protects) or pushed the whole page
 * sideways. `ellipsis` was set on two columns repo-wide, so wrapping was the
 * normal case.
 *
 * The 25 that did set one had split into two conventions: `x: 'max-content'`
 * in the clinic panel, and a guessed pixel figure (900 / 1000 / 1100) in the
 * console. A number is a guess in both directions — it forces a scrollbar onto
 * a wide screen that did not need one, and truncates on a narrow one that
 * needed more. `max-content` is measured rather than guessed, so it is the one
 * that becomes the default here.
 *
 * What it costs, read out of `rc-table@7.54.0` rather than assumed:
 *
 *  • `Table.js:289-290` sets `width: max-content; min-width: 100%` on the
 *    `<table>`, so a table narrower than its container still fills it and only
 *    a table wider than its container scrolls. Nothing gets narrower.
 *  • `Table.js:446-463` — `max-content` is explicitly excluded from the
 *    `table-layout: fixed` switch (`fixColumn ? … 'auto' : 'fixed'`), so column
 *    sizing is unchanged. A pixel `scroll.x` would NOT have been safe here for
 *    that reason.
 *
 * A caller that passes any `scroll` keeps it exactly, including a `{ y }` for a
 * fixed-height body — the `x` is only filled in where it was absent.
 */
const DEFAULT_SCROLL_X = 'max-content';
function withOverflowStrategy(scroll) {
    if (scroll === undefined)
        return { x: DEFAULT_SCROLL_X };
    if (scroll.x !== undefined)
        return scroll;
    return { ...scroll, x: DEFAULT_SCROLL_X };
}
export function Table({ columns, expandable, pagination, scroll, showSorterTooltip = false, ...rest }) {
    const dir = useUiDirection();
    const decorated = useMemo(() => (columns ? decorateColumns(columns, dir) : undefined), [columns, dir]);
    const mergedExpandable = expandable
        ? { ...expandable, expandIcon: expandable.expandIcon ?? defaultExpandIcon }
        : undefined;
    const mergedPagination = pagination === false
        ? false
        : {
            showTotal: persianShowTotal,
            itemRender: persianItemRender,
            ...pagination,
            // The page-size `<Select>` is rendered by AntD inside `<Pagination>`,
            // so the kit's own `Select` wrapper never sees it and it draws an
            // `@ant-design/icons` caret. Same normalisation the standalone
            // `<Pagination>` applies — see navigation.tsx.
            showSizeChanger: withRegistrySizeChangerIcon(pagination?.showSizeChanger),
        };
    return (_jsx(InTableContext.Provider, { value: true, children: _jsx(AntdTable, { ...rest, 
            // AntD's «برای مرتب‌سازی کلیک کنید» bubble sits on every sortable
            // header, outside the kit's delay group; `aria-sort` and the arrow
            // already say it. A caller that wants it back sets it.
            showSorterTooltip: showSorterTooltip, columns: decorated, expandable: mergedExpandable, pagination: mergedPagination, scroll: withOverflowStrategy(scroll) }) }));
}
Table.Column = AntdTable.Column;
Table.ColumnGroup = AntdTable.ColumnGroup;
Table.Summary = AntdTable.Summary;
Table.EXPAND_COLUMN = AntdTable.EXPAND_COLUMN;
Table.SELECTION_COLUMN = AntdTable.SELECTION_COLUMN;
Table.SELECTION_ALL = AntdTable.SELECTION_ALL;
Table.SELECTION_INVERT = AntdTable.SELECTION_INVERT;
Table.SELECTION_NONE = AntdTable.SELECTION_NONE;
function defaultExpandIcon({ expanded, onExpand, record, }) {
    return (_jsx("button", { type: "button", className: "bk-table-expand", "aria-expanded": expanded, "aria-label": expanded ? 'بستن جزئیات' : 'نمایش جزئیات', onClick: (event) => {
            onExpand(record, event);
            event.stopPropagation();
        }, children: _jsx(Icon, { name: expanded ? 'chevron-down' : 'chevron-forward', size: 16 }) }));
}
/**
 * «۱–۲۰ از ۳٬۴۰۰». Empty when there is nothing, rather than «۰ از ۰».
 *
 * The range is a `<Numeric>` and not a template string, and that is not
 * decoration. U+2013 EN DASH is bidi class ON — a neutral — so «۱–۲۰» in an RTL
 * paragraph resolves to two separate numbers with a neutral between them and is
 * laid out with the ۲۰ on the left. Same defect as the phone number; see
 * `components/bidi.tsx`. The total is a single run and does not need it, but it
 * gets the tabular figures either way.
 */
export function persianShowTotal(total, range) {
    if (total === 0)
        return '';
    return (_jsxs(_Fragment, { children: [_jsxs(Numeric, { children: [toPersianDigits(range[0]), RANGE_DASH, toPersianDigits(range[1])] }), ' از ', _jsx(Numeric, { children: toPersianDigits(total) })] }));
}
/**
 * Persian page numbers and directional prev/next arrows.
 *
 * AntD's own prev/next are the literal characters `‹` and `›`, which do not
 * mirror: `‹` is drawn pointing left in every direction. In RTL that makes the
 * "previous page" control point away from the previous page. `arrow-back` and
 * `arrow-forward` resolve against the document, so they always point the way
 * the pages actually move.
 */
export function persianItemRender(page, type, element) {
    if (type === 'page')
        return _jsx("a", { children: toPersianDigits(page) });
    if (type === 'prev')
        return _jsx(Icon, { name: "arrow-back", size: 16, label: "\u0635\u0641\u062D\u0647\u0654 \u0642\u0628\u0644" });
    if (type === 'next')
        return _jsx(Icon, { name: "arrow-forward", size: 16, label: "\u0635\u0641\u062D\u0647\u0654 \u0628\u0639\u062F" });
    /**
     * ── THE FIVE-PAGE JUMP ──────────────────────────────────────────────────
     *
     * `jump-prev` / `jump-next` used to fall through to `element`, which is
     * AntD's own `anticon-double-left` / `anticon-double-right` — a second icon
     * set, and a PHYSICAL one: `double-left` on "jump back" points the wrong way
     * in this document. It is invisible until you hover the `•••`, which is why
     * it survived a source audit and a visual pass; a `.anticon` sweep over the
     * rendered DOM found it.
     *
     * The markup is AntD's own two-layer structure rather than a bare icon:
     * `.ant-pagination-item-container` shows the ellipsis at rest and swaps to
     * `.ant-pagination-item-link-icon` on hover, purely in CSS. Returning a lone
     * glyph would replace the `•••` with a permanent arrow.
     */
    if (type === 'jump-prev' || type === 'jump-next') {
        const back = type === 'jump-prev';
        return (_jsx("a", { className: "ant-pagination-item-link", children: _jsxs("div", { className: "ant-pagination-item-container", children: [_jsx(Icon, { name: back ? 'arrow-circle-back' : 'arrow-circle-forward', size: 16, className: "ant-pagination-item-link-icon", label: back ? 'پنج صفحه قبل' : 'پنج صفحه بعد' }), _jsx("span", { className: "ant-pagination-item-ellipsis", children: "\u2022\u2022\u2022" })] }) }));
    }
    return element;
}
/**
 * ── THE PAGE-SIZE SELECT IS AntD's, NOT THE KIT's ───────────────────────────
 *
 * `<Pagination showSizeChanger />` renders a `<Select>` that the kit never
 * touches, and that Select draws `<span class="anticon anticon-down">` — a
 * second icon set, on the footer of every list screen in both panels. Neither
 * the lint rule nor the source scan can see it: no file imports the library.
 * Counted in Chrome on the console's clinic list before this existed.
 *
 * `showSizeChanger` accepts `SelectProps` (antd ≥ 5.21), which is the only
 * seam. `true` becomes `{ suffixIcon }`; an object keeps a `suffixIcon` it
 * already chose; `false`/absent stays untouched so the changer's visibility is
 * still entirely the caller's call.
 *
 * Exported because `<Table>`'s footer builds its own pagination object and has
 * to apply the same rule — one definition, two call sites.
 */
export function withRegistrySizeChangerIcon(showSizeChanger) {
    if (showSizeChanger === true) {
        return {
            suffixIcon: _jsx(Icon, { name: "chevron-down", size: 16 }),
            ...PERSIAN_SIZE_LABELS,
        };
    }
    if (showSizeChanger && typeof showSizeChanger === 'object') {
        return {
            ...PERSIAN_SIZE_LABELS,
            ...showSizeChanger,
            suffixIcon: showSizeChanger.suffixIcon ?? _jsx(Icon, { name: "chevron-down", size: 16 }),
        };
    }
    return showSizeChanger;
}
/**
 * «۲۰ / صفحه», not «20 / صفحه».
 *
 * rc-pagination builds each option's text as `${size} ${items_per_page}` with
 * a JS number, so the page-size changer was the one place on every list screen
 * that still printed Latin digits beside Persian page numbers. The label and
 * the popup rows are re-rendered through `toPersianDigits`; the option VALUES
 * stay numbers, so nothing about the page size itself changes.
 */
function persianise(label) {
    return typeof label === 'string' || typeof label === 'number' ? toPersianDigits(label) : label;
}
const PERSIAN_SIZE_LABELS = {
    labelRender: (props) => persianise(props.label ?? props.value),
    optionRender: (option) => persianise(option.label ?? option.value),
};
//# sourceMappingURL=table.js.map